import './TripPlannerStyles-Colors.css';
import './TripPlannerStyles.css';
import moment from 'moment';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { DropdownItemProps, DropdownOnSearchChangeData, DropdownProps, Icon, Message, Segment } from 'semantic-ui-react';
import { AgencyStateType } from '../../actions/actionTypes';
import { autoCompleteAddress, buildTripStartingAt, findAddress } from '../../actions/tripPlannerActions';
import Api from '../../api/api';
import { AppState } from '../../reducers';
import { getSelectedOrDefaultAgency } from '../../selectors';
import { InfoboxProps, PolylineProps, PushpinProps } from '../../types/MapModels';
import TransitMap from '../shared/AzureMap/TransitMap';
import Itineraries, { GetNormalTimeString, ItineraryData } from './ItineraryItem';
import { MapPoint } from './MapPoint';
import { MapPointKind } from './MapPointKind';
import TripPlannerInputs from './TripPlannelInputs';
import { TripPlannerContext } from './TripPlannerContext';

export interface TrackPolylineProps extends PolylineProps {
    options: { strokeColor?: string; strokeThickness?: number; [key: string]: any; };
}

type Props = {
    agency: AgencyStateType | undefined;
};

function getCurrentTime() {
    const r = GetNormalTimeString(new Date());
    return r;
}

interface TripPlannerAddressOption extends DropdownItemProps {
    hubId?: string;
}

interface TripPlannerAddressDropDownStateType {
    options: TripPlannerAddressOption[];
    selectedValue: string | undefined;
}

export interface AddressSearchCriteria {
    searchQuery: string;
    searchTime: number;
    setAddressesState: React.Dispatch<React.SetStateAction<TripPlannerAddressDropDownStateType>>;
    addressLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

const initialAddressesState: TripPlannerAddressDropDownStateType = {
    options: [],
    selectedValue: '',
};

function cloneInfoboxes(items: InfoboxProps[]): InfoboxProps[] {
    return items.map(item => ({
        ...item,
        options: item.options ? { ...item.options } : undefined,
    }));
}

const TripPlannerForm: React.FC<Props> = ({ agency }) => {
    const [gstate, setGstate] = useState<TripPlannerContext>(TripPlannerContext.EMPTY);
    const [infoboxes, setInfoboxes] = useState<InfoboxProps[]>([]);
    const [pushpins, setPushpins] = useState<PushpinProps[]>([]);
    const [polylines, setPolylines] = useState<PolylineProps[]>([]);

    const [time, setTime] = useState<string>('');
    const [date, setDate] = useState<string>(moment(new Date().setMinutes(0, 0, 0)).format('YYYY-MM-DD'));
    const [itineraries, setItineraries] = useState<ItineraryData[] | null>(null);
    const [selectedItinerary, setSelectedItinerary] = useState<ItineraryData | null>(null);
    const [loadingItineraries, setLoadingItineraries] = useState(false);
    const [fromAddressessState, setFromAddressesState] = useState(initialAddressesState);
    const [toAddressessState, setToAddressesState] = useState(initialAddressesState);
    const [addressSearchCriteriaState, setAddressSearchCriteriaState] = useState<AddressSearchCriteria>();
    const [addressesNoResultMessageState, setAddressesNoResultMessageState] = useState<string>('');
    const [fromAddressLoadingState, setFromAddressLoadingState] = useState(false);
    const [toAddressLoadingState, setToAddressLoadingState] = useState(false);

    const [center, setCenter] = useState<CoordinatePair>();

    useEffect(() => {
        const timer = setTimeout(async () => {
            if (addressSearchCriteriaState && addressSearchCriteriaState.searchTime) {
                const currentTime = new Date().getTime();
                if ((currentTime - addressSearchCriteriaState.searchTime) / 1000 > 1) {
                    await updateInputAddress(addressSearchCriteriaState);
                }
            }
        }, 1000);
        return () => clearTimeout(timer);
    }, [addressSearchCriteriaState]);

    const handleExchangeClick = () => {
        if (gstate.exchangePoints()) {
            //setOriginPoint(gstate.origin);
            setFromAddressesState(toAddressessState);
            setToAddressesState(fromAddressessState);

            //setTargetPoint(gstate.target);

            setPushpins(gstate.getPushpins());
            setInfoboxes(cloneInfoboxes(gstate.getInfoboxes()));
        }
    };
    const handleResetClick = () => {
        setPolylines(gstate.updateItinerary(null));
        setSelectedItinerary(null);
        setInfoboxes(cloneInfoboxes(gstate.getInfoboxes(true)));
        setItineraries(null);

        if (gstate.resetPoints()) {
            //setOriginPoint(gstate.origin);
            setFromAddressesState(initialAddressesState);

            //setTargetPoint(gstate.target);
            setToAddressesState(initialAddressesState);

            setPushpins(gstate.getPushpins());
            setInfoboxes(cloneInfoboxes(gstate.getInfoboxes()));
        }
    };
    const handleGoClick = async () => {
        setLoadingItineraries(true);
        setSelectedItinerary(null);
        gstate.storeEndpointPushpins();
        setPushpins(gstate.getPushpins(true));
        setInfoboxes(cloneInfoboxes(gstate.getInfoboxes(true)));
        setItineraries(null);
        try {
            let result: ItineraryData[] = [];
            if (!!gstate.origin && !!gstate.target) {
                const data = await buildTripStartingAt(
                    gstate.origin?.location.latitude,
                    gstate.origin?.location.longitude,
                    gstate.target?.location.latitude,
                    gstate.target?.location.longitude,
                    time,
                    true,
                    true,
                    true,
                    false,
                    agency?.id,
                    gstate.origin?.hubId,
                    gstate.target?.hubId);
                result = data?.map(source => new ItineraryData(source, false)) || [];
            }

            setItineraries(result);
            const itinerary = (result && result[0]) || null;
            setSelectedItinerary(itinerary);

            setLoadingItineraries(false);
        }
        catch (e) {
            console.error('TripPlanner', e);
            setLoadingItineraries(false);
        }
    };

    useEffect(() => {
        const polylines = gstate.updateItinerary(selectedItinerary);
        setPolylines(prev => prev === polylines ? prev : polylines);
        const infoboxes = gstate.getInfoboxes();
        const nextInfoboxes = cloneInfoboxes(infoboxes);
        setInfoboxes(prev => prev === nextInfoboxes ? prev : nextInfoboxes);
    }, [selectedItinerary, gstate]);

    const handleItineraryClick = (itinerary: ItineraryData) => {
        setSelectedItinerary(itinerary);
    };

    const handleFromAddressChange = (_event: React.SyntheticEvent<HTMLElement, Event>, { value: addressId }: DropdownProps) => {
        setFromAddressesState(prevState => ({
            ...prevState,
            selectedValue: addressId as string,
        }));
        const addressData = fromAddressessState.options.find(o => o.value === addressId);
        if (!addressData?.value)
            return;
        const coordsStr = addressData.value as string;
        const [lat, lon] = coordsStr.split(':').map(Number);
        const location: CoordinatePair = [lat as Latitude, lon as Longitude];
        drawPointOnTheMap(location, addressData.text as string, getOptionHubId(addressData));
        onPointChoice('origin');
    };

    const handleToAddressChange = (_event: React.SyntheticEvent<HTMLElement, Event>, { value: addressId }: DropdownProps) => {
        setToAddressesState(prevState => ({
            ...prevState,
            selectedValue: addressId as string,
        }));
        const addressData = toAddressessState.options.find(o => o.value === addressId);
        if (!addressData?.value)
            return;
        const coordsStr = addressData.value as string;
        const [lat, lon] = coordsStr.split(':').map(Number);
        const location: CoordinatePair = [lat as Latitude, lon as Longitude];
        drawPointOnTheMap(location, addressData.text as string, getOptionHubId(addressData));
        onPointChoice('target');
    };

    const handleFromSearchChange = (_event: React.SyntheticEvent<HTMLElement, Event>, { searchQuery }: DropdownOnSearchChangeData) => {
        const searchTime = new Date().getTime();
        setAddressSearchCriteriaState({
            searchQuery,
            searchTime,
            setAddressesState: setFromAddressesState,
            addressLoading: setFromAddressLoadingState,
        });
    };
    const handleToSearchChange = (_event: React.SyntheticEvent<HTMLElement, Event>, { searchQuery }: DropdownOnSearchChangeData) => {
        const searchTime = new Date().getTime();
        setAddressSearchCriteriaState({
            searchQuery,
            searchTime,
            setAddressesState: setToAddressesState,
            addressLoading: setToAddressLoadingState,
        });
    };

    const updateInputAddress = async ({ searchQuery, setAddressesState, addressLoading }: AddressSearchCriteria) => {
        if (agency === undefined) return;
        try {
            addressLoading(true);
            const addresses = await autoCompleteAddress(agency.id, searchQuery);
            if (addresses.length > 0) {
                setAddressesState({
                    options: addresses.map(a => ({
                        value: `${a.lat}:${a.lon}`,
                        text: a.placeType === 'TransitHub' ? `${a.fullAddress} (Transit Center)` : a.fullAddress,
                        hubId: a.placeType === 'TransitHub' ? a.hubId : undefined,
                    })),
                    selectedValue: '',
                });
            } else {
                setAddressesState(initialAddressesState);
                setAddressesNoResultMessageState('No addresses were found');
            }
        }
        catch {
            setAddressesState(initialAddressesState);
        }
        finally {
            addressLoading(false);
        }
    };

    const onPointChoice = (choice: 'origin' | 'target') => {
        let point: MapPoint | null;
        switch (choice) {
            case 'origin':
                point = gstate.setInputChoice(MapPointKind.from);
                if (point) {
                    setFromAddressesState({
                        options: [{ value: `${point.location.latitude}:${point.location.longitude}`, text: point.address }],
                        selectedValue: `${point.location.latitude}:${point.location.longitude}`,
                    });
                } else {
                    setFromAddressesState(initialAddressesState);
                }
                break;
            case 'target':
                point = gstate.setInputChoice(MapPointKind.to);
                if (point) {
                    setToAddressesState({
                        options: [{ value: `${point.location.latitude}:${point.location.longitude}`, text: point.address }],
                        selectedValue: `${point.location.latitude}:${point.location.longitude}`,
                    });
                } else {
                    setToAddressesState(initialAddressesState);
                }
                break;
            default:
                console.error(`TripPlanner: invalid choice value (${choice})!`);
                return;
        }
        if (point) {
            setPushpins(gstate.getPushpins());
            setInfoboxes(cloneInfoboxes(gstate.getInfoboxes(true)));
        }
    };
    gstate.onPointChoice = onPointChoice;

    const handleMapClick = async (e: any) => {
        const map = e.target as any;
        const eventPoint: [number, number] = [e.getX?.() ?? e.pageX, e.getY?.() ?? e.pageY];
        const loc = map?.tryPixelToLocation ? map.tryPixelToLocation(eventPoint) : null;
        if (!loc) return;

        const location: CoordinatePair = Array.isArray(loc) ? [loc[0].latitude ?? loc[0][0], loc[0].longitude ?? loc[0][1]] : [loc.latitude, loc.longitude];
        const addressStr = await findAddress(location[0], location[1]);
        drawPointOnTheMap(location, addressStr);
    };
    const drawPointOnTheMap = (location: CoordinatePair, addressStr: string, hubId?: string | null) => {
        const point = gstate.setInputPoint({ latitude: location[0], longitude: location[1] } as any);
        point.setHubId(hubId ?? null);
        setPushpins(gstate.getPushpins());
        setInfoboxes(cloneInfoboxes(gstate.getInfoboxes()));
        if (addressStr) {
            point.setAddress(gstate, addressStr);
            setPushpins(gstate.getPushpins(true));
            setInfoboxes(cloneInfoboxes(gstate.getInfoboxes(true)));
        }
    };

    const getOptionHubId = (option: TripPlannerAddressOption) => option.hubId ?? null;

    useEffect(() => {
        if (agency === undefined) return;
        (async (agencyId: string) => {
            handleResetClick();
            const { Latitude, Longitude } = await Api.getOrgLocation(agencyId);
            setCenter([Latitude, Longitude]);
            setTime(getCurrentTime());
        })(agency.id);
    }, [agency]);
    useEffect(() => {
        if (gstate === TripPlannerContext.EMPTY) {
            const actualContext = new TripPlannerContext();
            setGstate(actualContext);
        }
    }, [gstate]);
    return (
        <div className="tp-container">
            <div className="tp-pane map">
                <Segment.Group className="trip-planner">
                    <Segment className="trip-planner-form">
                        <TripPlannerInputs
                            time={time} onTimeChanged={v => setTime(v)}
                            date={date} onDateChanged={v => setDate(v)}
                            onExchange={handleExchangeClick}
                            onReset={handleResetClick}
                            onGo={handleGoClick}
                            fromAddressState={fromAddressessState}
                            toAddressState={toAddressessState}
                            noResultAddressMessage={addressesNoResultMessageState}
                            handleFromSearchChange={handleFromSearchChange}
                            handleToSearchChange={handleToSearchChange}
                            fromAddressLoadingState={fromAddressLoadingState}
                            toAddressLoadingState={toAddressLoadingState}
                            handleFromAddressChange={handleFromAddressChange}
                            handleToAddressChange={handleToAddressChange}
                        />
                    </Segment>
                    <Segment className="trip-planner-map">
                        <TransitMap
                            //div={{ style: styles.map }}
                            map={{
                                center,
                                options: {
                                    zoom: 14,
                                    disableAutoCamera: !selectedItinerary,
                                },
                                eventHandlers: [
                                    {
                                        event: 'click',
                                        callback: handleMapClick,
                                    },
                                ],
                            }}
                            infoboxes={infoboxes}
                            pushpins={pushpins}
                            polylines={polylines}
                        />
                    </Segment>
                </Segment.Group>
            </div>
            <div className="tp-pane inf">
                {loadingItineraries
                    ? (<Message info icon>
                        <Icon name="circle notched" loading />
                        <Message.Content>
                            <Message.Header>Loading itineraries</Message.Header>
                        </Message.Content>
                    </Message>)
                    : !itineraries
                        ? (<Message info>
                            <Message.Content>
                                <Message.Header>How to Use</Message.Header>
                                <p>Click or double-click on the map to select your destination and starting points.</p>
                                <p>Or type them in the boxes above and press “GO”</p>
                            </Message.Content>
                        </Message>)
                        : itineraries.length === 0
                            ? (<Message info>
                                <Message.Content>
                                    <Message.Header>No Itineraries Found</Message.Header>
                                </Message.Content>
                            </Message>)
                            : (<Segment>
                                <Itineraries
                                    itinerares={itineraries}
                                    selected={selectedItinerary}
                                    onClick={handleItineraryClick} />
                            </Segment>)
                }
            </div>
        </div>
    );
};

export default connect(
    (state: AppState) => ({
        agency: getSelectedOrDefaultAgency(state),
    }),
)(TripPlannerForm);
